import {
  ForbiddenCodeEnum,
  hasCode,
  isForbiddenError,
  isSuccess,
} from '@/context/services/type';
import { isCommonError } from '@/libs/typeguard/isError';
import {
  CommonError,
  PostOfficeWithdrawalRequestWithdrawalFeedbackForm,
  useGetOfficeWithdrawalQuestionsSuspense,
  usePostOfficeWithdrawal,
} from 'ap-openapi';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

export enum WithdrawalStatus {
  SUCCESS = 'success',
  INVALID = 'invalid',
  COMMON_ERROR = 'commonError',
  FORBIDDEN = 'forbidden',
  FORBIDDEN_MIMIC_MODE = 'forbiddenMimicError',
}

type WithdrawalSuccess = {
  status: WithdrawalStatus.SUCCESS;
  redirect_url: string;
};

type WithdrawalForbidden = {
  status: WithdrawalStatus.FORBIDDEN;
  redirect_url?: string;
  error?: CommonError;
};

type WithdrawalForbiddenMimicMode = {
  status: WithdrawalStatus.FORBIDDEN_MIMIC_MODE;
  error?: CommonError;
};

type WithdrawalError = {
  status: WithdrawalStatus.COMMON_ERROR;
  error: CommonError;
};

type WithdrawalResult =
  | WithdrawalSuccess
  | WithdrawalForbidden
  | WithdrawalForbiddenMimicMode
  | WithdrawalError;

export const useGetOfficeWithdrawalQuestions = () => {
  const { data } = useGetOfficeWithdrawalQuestionsSuspense();
  return data?.data?.withdrawal_questions ?? [];
};

export const usePostWithdrawal = () => {
  const { mutateAsync: _mutateAsync } = usePostOfficeWithdrawal();
  const { t } = useTranslation();

  const mutateAsync = useCallback(
    async (
      withdrawal_feedback_form: PostOfficeWithdrawalRequestWithdrawalFeedbackForm
    ): Promise<WithdrawalResult> => {
      const response = await _mutateAsync({
        data: { withdrawal_feedback_form },
      });

      if (isSuccess(response.status)) {
        return {
          status: WithdrawalStatus.SUCCESS,
          redirect_url: response.headers.location,
        };
      } else if (isForbiddenError(response.status)) {
        if (
          hasCode(response.data) &&
          response.data.code === ForbiddenCodeEnum.FORBIDDEN_MIMIC_MODE
        ) {
          return {
            status: WithdrawalStatus.FORBIDDEN_MIMIC_MODE,
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
            error: response.data as unknown as CommonError,
          };
        }
        return {
          status: WithdrawalStatus.FORBIDDEN,
          redirect_url: response?.headers?.location ?? undefined,
          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
          error: response.data as unknown as CommonError,
        };
      } else {
        return {
          status: WithdrawalStatus.COMMON_ERROR,
          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
          error: isCommonError(
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
            response.data as unknown as Record<string, unknown>
          )
            ? // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
              (response.data as unknown as CommonError)
            : { messages: [t('system_error')] },
        };
      }
    },
    [_mutateAsync, t]
  );
  return { mutateAsync };
};
